import { LoginCredentials, LoginResponse } from '../types/login.type';
import { Observable, of, from } from 'rxjs';
import { PassDetails, PassInformationRequestObject } from '../types/pass.type';
import { RouteStatusUpdate, RouteStatusUpdateResult } from '../types/route-status-update.type';
import { LiveTelemetryPayload, LiveTelemetryResponse } from '../types/live-telemetry.type';

import { AppData } from '../data';
import { Device } from '../types/device.type';
import { GpsReport } from '../types/gps-report.type';
import { Injectable, inject } from '@angular/core';
import { NativePluginService } from './plugin/native-plugins.service';
import { RegistrationResult } from '../types/registration.type';
import { RouteSummary } from '../types/route-summary.type';
import { RouteUpdate } from '../types/route-update.type';
import { SafeHttpService } from './safe-http.service';
import { environment } from '../../environments/environment';
import { DepositFundsRequest, DepositFundsResponse } from '../types/deposit-funds.type';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { TrackJS } from "trackjs";

@Injectable({
  providedIn: 'root',
})
export class ServerProxyService {
  private readonly data = inject(AppData);
  private safeHttp = inject(SafeHttpService);
  private pluginService = inject(NativePluginService);

  paraScopeGpsReportingEndpoint: string;
  paraScopeGpsReportingEndpointToken: string;

  scenario: string = null;

  private tokenCode() {
    if (!!this.paraScopeGpsReportingEndpointToken) {
      return `?code=${this.paraScopeGpsReportingEndpointToken}`;
    }
    return '';
  }

  riderImage(sessionId: string, riderImageId: string): Observable<string> {
    return this.safeHttp.getAsync<string>(this.dispatcherUrl + `riderImage/${sessionId}/${riderImageId}`);
  }

  registerDevice(device: Device): Observable<RegistrationResult> {
    return this.safeHttp.postAsync<RegistrationResult>(this.dispatcherUrl + 'registerDevice', device);
  }

  login(login: LoginCredentials): Observable<LoginResponse> {
    return this.safeHttp.postAsync<LoginResponse>(this.dispatcherUrl + `login?scenario=${this.scenario}`, login);
  }

  resetSession(sessionId: string): Observable<LoginResponse> {
    return this.safeHttp.getAsync(this.dispatcherUrl + `resetRoute/${sessionId}`);
  }

  startRoute(routeId: number, gpsReport: GpsReport): Promise<RouteUpdate> {
    return this.safeHttp.post<RouteUpdate>(this.dispatcherUrl + `startRoute?scenario=${this.scenario}`,
      {routeId, gpsReport, sessionId: this.sessionId });
  }

  routePreview(routeId: number): Promise<RouteUpdate> {
    return this.safeHttp.post<RouteUpdate>(this.dispatcherUrl + `routePreview`,
      { routeId: routeId, sessionId: this.sessionId });
  }

  routeUpdate(scenario?: string): Promise<RouteUpdate> {
    return this.safeHttp.get<RouteUpdate>(this.dispatcherUrl + `routeUpdate/${this.sessionId}?scenario=${this.scenario}`);
  }

  routeStatusUpdate(pendingUpdates: RouteStatusUpdate): Promise<RouteStatusUpdateResult> {
    return this.safeHttp.post<RouteStatusUpdateResult>(this.dispatcherUrl + `routeStatusUpdate?scenario=${this.scenario}`, pendingUpdates)
      .catch(async err => {
        if (pendingUpdates.retryCount >= 3) { throw(err); }
        return await new Promise(resolve => {
          setTimeout(async () => {
            pendingUpdates.retryCount++;
            resolve(await this.routeStatusUpdate(pendingUpdates));
            //  caps out the timeout on 30 seconds
          }, Math.min(1_000 * Math.pow(2, pendingUpdates.retryCount ?? 0), 10_000));
        });
      });
  }

  routeSummaries(): Promise<RouteSummary[]> {
    return this.safeHttp.get<RouteSummary[]>(this.dispatcherUrl + `routeSummaries/${this.sessionId}`);
  }

  tripSync(customerId: number): Promise<Boolean> {
    return this.safeHttp.get(this.dispatcherUrl + `IsTripSync/${customerId}`);
  }

  passInformation(passInformationRequest: PassInformationRequestObject): Promise<PassDetails> {
    return this.safeHttp.post<PassDetails>(this.dispatcherUrl + `passInformation`, passInformationRequest, null, 60000);
  }

  /** Add funds to user balance */
  depositFunds(request: DepositFundsRequest): Promise<DepositFundsResponse> {
    return this.safeHttp.post<DepositFundsResponse>(this.dispatcherUrl + 'depositFunds', request);
  }

  sendLiveTelemetry(payload: LiveTelemetryPayload): Observable<LiveTelemetryResponse> {
    if (!environment.features.liveTelemetry.enabled) {
      return of(null);
    }

    const endpoint = this.data.localSettings.isProduction
      ? environment.features.liveTelemetry.prodEndpoint
      : environment.features.liveTelemetry.devEndpoint;
    TrackJS.addMetadata('telemetryUrl', endpoint)

    if (!endpoint) {
      console.warn('[ServerProxyService] No telemetry URL configured, skipping telemetry send');
      return of(null);
    }

    return from(
      CapacitorHttp.post({
        url: endpoint,
        headers: {
          'Content-Type': 'application/json',
        },
        data: payload,
      }).then((response: HttpResponse) => {
        if (response.status >= 200 && response.status < 300) {
          return response.data as LiveTelemetryResponse;
        } else {
          throw new Error(`HTTP ${response.status}: ${response.data}`);
        }
      })
    );
  }

  private get dispatcherUrl(): string {
    const env = this.data.localSettings.dispatcherUrl;
    if (env) {
      return env;
    } else {
      console.warn(`No environment found for "${this.data.localSettings.environment}"`);
      //see also: environment.ecolane.ts
      if (this.pluginService.isNativePlatform && environment.features.defaultEnvToProd) {
        console.info(`Using "Production Live": "${environment.dispatcherEndpoints[0].name}"`);
        return environment.dispatcherEndpoints[1].url;//Prod environment named "Production Live"
      }
      console.info(`Using first environment in list: "${environment.dispatcherEndpoints[0].name}"`);
      return environment.dispatcherEndpoints[0].url;

    }
  }

  private get sessionId(): string {
    return this.data.sessionId;
  }
}
